import React, { useState, useEffect } from 'react';
import { BoostingOrder } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { Zap, ShieldCheck, CheckCircle, Clock, ArrowRight, Play, Check } from 'lucide-react';

export const BoosterDashboard: React.FC = () => {
  const { user } = useAuth();
  const [availableOrders, setAvailableOrders] = useState<BoostingOrder[]>([]);
  const [loading, setLoading] = useState(true);

  // Progress update state
  const [progressVal, setProgressVal] = useState(65);
  const [boosterNotes, setBoosterNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchAvailable = async () => {
    try {
      const res = await fetch('/api/booster/available');
      if (res.ok) {
        const data = await res.json();
        if (data.success) setAvailableOrders(data.availableOrders || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailable();
  }, []);

  const handleClaim = async (id: string) => {
    try {
      const res = await fetch(`/api/booster/claim/${id}`, { method: 'POST' });
      if (res.ok) {
        await fetchAvailable();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateProgress = async (id: string, isCompleted: boolean) => {
    setUpdating(true);
    try {
      const res = await fetch(`/api/booster/progress/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          progressPercent: isCompleted ? 100 : progressVal,
          notes: boosterNotes || 'Win streak maintained today.',
          isCompleted
        })
      });
      if (res.ok) {
        await fetchAvailable();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Header */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-lg flex items-center justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 mb-2">
            <Zap className="w-3.5 h-3.5" />
            <span>Pro Booster Console</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-brand-lightText dark:text-brand-darkText">
            Booster Hub ({user?.username})
          </h1>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Claim available customer boosting jobs, post progress, and fulfill rank milestones
          </p>
        </div>
      </div>

      {/* Available Jobs to Claim */}
      <div className="space-y-4">
        <h2 className="font-extrabold text-xl text-brand-lightText dark:text-brand-darkText">
          Available Boosting Orders ({availableOrders.length})
        </h2>

        {availableOrders.length > 0 ? (
          <div className="space-y-4">
            {availableOrders.map((order) => (
              <div
                key={order.id}
                className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
              >
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold">#{order.order_number}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/15 text-amber-600">
                      Unclaimed Job
                    </span>
                  </div>

                  <h3 className="font-bold text-base">
                    {order.game_name} • {order.current_rank} ➔ {order.target_rank}
                  </h3>

                  <p className="text-xs text-brand-lightSecText">
                    Region: <strong>{order.server_region}</strong> • Mode: <strong>{order.is_duo ? 'Duo Queue' : 'Solo Pilot'}</strong> • Stream: <strong>{order.stream_requested ? 'Yes' : 'No'}</strong>
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <span className="font-black text-xl text-emerald-500">
                    ${((order.total_cents || 5400) * 0.70 / 100).toFixed(2)} Payout
                  </span>

                  <button
                    onClick={() => handleClaim(order.id)}
                    className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue transition-all"
                  >
                    Claim Job
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 rounded-3xl bg-white dark:bg-brand-darkCard border text-center text-xs text-brand-lightSecText">
            No unclaimed orders right now. Check back as new customers place orders!
          </div>
        )}
      </div>

      {/* Active Boost Demo Fulfillment Tool */}
      <div className="p-8 rounded-3xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
        <div>
          <h2 className="font-extrabold text-lg text-brand-lightText dark:text-brand-darkText">
            Active Job Progress Control (Demo Job: bst_demo_1)
          </h2>
          <p className="text-xs text-brand-lightSecText mt-1">
            Valorant Platinum 2 ➔ Diamond 1 (Customer: SaraStriker)
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs font-bold mb-1">
              <span>Progress Percentage:</span>
              <span className="text-brand-blue">{progressVal}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progressVal}
              onChange={(e) => setProgressVal(parseInt(e.target.value))}
              className="w-full accent-brand-blue cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Booster Status Note</label>
            <input
              type="text"
              value={boosterNotes}
              onChange={(e) => setBoosterNotes(e.target.value)}
              placeholder="e.g. Promoted to Plat 3 with 30-12 KDA. Continuing to Diamond tomorrow."
              className="w-full p-2.5 text-xs rounded-xl bg-white dark:bg-brand-darkCard border"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => handleUpdateProgress('bst_demo_1', false)}
              disabled={updating}
              className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-brand-blue"
            >
              Update Progress
            </button>
            <button
              onClick={() => handleUpdateProgress('bst_demo_1', true)}
              disabled={updating}
              className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-emerald-600"
            >
              Mark 100% Completed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
